import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Flame, Trophy } from "lucide-react";
import { HistoryEntryRow } from "@/components/history/history-entry";
import { historyBoardQuery, historyMonthQuery } from "@/lib/history-queries";
import {
  HISTORY_PAGE_SIZE,
  monthFromSlug,
  monthSlug,
  monthYearLabel,
  stepMonth,
} from "@/lib/history";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { translate, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";
import { formatHeat } from "@/lib/behot";

export const Route = createFileRoute("/history/$year/$month/$board")({
  validateSearch: (search: Record<string, unknown>) => {
    const raw = Number(search["page"]);
    return { page: Number.isFinite(raw) && raw > 1 ? Math.floor(raw) : undefined };
  },
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ context, params, deps }) => {
    const year = Number(params.year);
    const month = monthFromSlug(params.month);
    if (!/^\d{4}$/.test(params.year) || !month) throw notFound();
    const result = await context.queryClient.ensureQueryData(
      historyBoardQuery(year, month, params.board, deps.page, HISTORY_PAGE_SIZE),
    );
    if (!result.snapshot) throw notFound();
    void context.queryClient.ensureQueryData(historyMonthQuery(year, month));
    return {
      year,
      month,
      page: deps.page,
      boardName: result.snapshot.category_name,
      topName: result.entries[0]?.listing_name ?? null,
      image: result.entries[0]?.image_url ?? null,
    };
  },
  head: ({ params, loaderData }) => {
    const locale = detectLocale();
    const month = monthFromSlug(params.month);
    const year = Number(params.year);
    const path = `/history/${params.year}/${params.month}/${params.board}`;
    if (!loaderData || !month) {
      return buildPageMetadata({
        path,
        title: "Unavailable | BeHot.Now",
        description: "This historical ranking is not available.",
        noindex: true,
      });
    }
    const monthYear = monthYearLabel(month, year, locale);
    const board = loaderData.boardName;
    const canonical =
      loaderData.page > 1 ? `${SITE_URL}${path}?page=${loaderData.page}` : `${SITE_URL}${path}`;
    return buildPageMetadata({
      path,
      canonical,
      title: translate(locale, "history.board.title", { board, monthYear }),
      description: translate(locale, "history.board.description", { board, monthYear }),
      ogTitle: translate(locale, "history.board.h1", { board, monthYear }),
      ogDescription: translate(locale, "history.board.description", { board, monthYear }),
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: [
        `top ${board} ${monthYear}`,
        `${board} rankings ${year}`,
        `best ${board} ${year}`,
        `BeHot.Now ${board} rankings`,
      ],
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: translate(locale, "history.nav"), path: "/history" },
        { name: params.year, path: `/history/${params.year}` },
        { name: monthYear, path: `/history/${params.year}/${params.month}` },
        { name: board, path },
      ],
    });
  },
  component: HistoryBoardPage,
});

function HistoryBoardPage() {
  const params = Route.useParams();
  const { year, month, page } = Route.useLoaderData();
  const { t, locale } = useT();
  const { data } = useSuspenseQuery(
    historyBoardQuery(year, month, params.board, page, HISTORY_PAGE_SIZE),
  );
  const { data: monthBoards } = useSuspenseQuery(historyMonthQuery(year, month));
  const monthYear = monthYearLabel(month, year, locale);
  const snapshot = data.snapshot;

  if (!snapshot) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("history.board.notFound")}</h1>
        <Link to="/history" className="mt-4 inline-block text-primary hover:underline">
          {t("history.backToHistory")}
        </Link>
      </div>
    );
  }

  const board = snapshot.category_name;
  const totalPages = Math.max(1, Math.ceil(data.total / HISTORY_PAGE_SIZE));
  const prev = stepMonth(year, month, -1);
  const next = stepMonth(year, month, 1);
  const others = monthBoards.filter((s) => s.category_slug !== snapshot.category_slug).slice(0, 12);

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("history.board.h1", { board, monthYear }),
    numberOfItems: data.total,
    itemListOrder: "https://schema.org/ItemListOrderDescending",
    itemListElement: data.entries.map((e) => ({
      "@type": "ListItem",
      position: e.position,
      name: e.listing_name,
      url: `${SITE_URL}/p/${e.listing_slug}`,
    })),
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <script
        type="application/ld+json"
        // ItemList of the frozen ranking, emitted in the SSR HTML for crawlers.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }}
      />

      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          BeHot.Now
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/history" className="hover:text-foreground">
          {t("history.nav")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/history/$year" params={{ year: String(year) }} className="hover:text-foreground">
          {year}
        </Link>
        <span className="px-1.5">/</span>
        <Link
          to="/history/$year/$month"
          params={{ year: String(year), month: monthSlug(month) }}
          className="capitalize hover:text-foreground"
        >
          {monthYear}
        </Link>
        <span className="px-1.5">/</span>
        <span className="text-foreground">{board}</span>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <Trophy className="size-3.5" />
          {t("history.pastRankings")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-black sm:text-4xl">
          {t("history.board.h1", { board, monthYear })}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t("history.board.intro", { board, monthYear })}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <span className="inline-flex items-center gap-1 font-semibold text-primary">
            <Flame className="size-4" />
            {formatHeat(snapshot.total_heat_cents)} {t("history.heat")}
          </span>
          <span className="text-muted-foreground">
            {t("history.entries", { n: snapshot.entry_count })}
          </span>
          <Link
            to="/c/$slug"
            params={{ slug: snapshot.category_slug }}
            className="font-medium text-primary hover:underline"
          >
            {t("history.board.current", { board })}
          </Link>
        </div>
      </header>

      <ol className="mt-8 space-y-2">
        {data.entries.map((e) => (
          <HistoryEntryRow key={e.id} entry={e} />
        ))}
      </ol>

      {totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="mt-6 flex items-center justify-between gap-3 text-sm"
        >
          {page > 1 ? (
            <Link
              to="/history/$year/$month/$board"
              params={params}
              search={{ page: page - 1 > 1 ? page - 1 : undefined }}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ArrowLeft className="size-4" />
              {t("history.prev")}
            </Link>
          ) : (
            <span />
          )}
          <span className="text-muted-foreground">
            {t("history.page", { n: page, total: totalPages })}
          </span>
          {page < totalPages ? (
            <Link
              to="/history/$year/$month/$board"
              params={params}
              search={{ page: page + 1 }}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              {t("history.next")}
              <ArrowRight className="size-4" />
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}

      {others.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-bold">
            {t("history.board.otherBoards", { monthYear })}
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {others.map((s) => (
              <li key={s.id}>
                <Link
                  to="/history/$year/$month/$board"
                  params={{
                    year: String(year),
                    month: monthSlug(month),
                    board: s.category_slug,
                  }}
                  search={{ page: undefined }}
                  className="inline-block rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:border-primary/50 hover:text-primary"
                >
                  {s.category_name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 text-sm">
        <Link
          to="/history/$year/$month/$board"
          params={{
            year: String(prev.year),
            month: monthSlug(prev.month),
            board: snapshot.category_slug,
          }}
          search={{ page: undefined }}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <ArrowLeft className="size-4" />
          {t("history.board.previousMonth")}
        </Link>
        <Link
          to="/history/$year/$month/$board"
          params={{
            year: String(next.year),
            month: monthSlug(next.month),
            board: snapshot.category_slug,
          }}
          search={{ page: undefined }}
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          {t("history.board.nextMonth")}
          <ArrowRight className="size-4" />
        </Link>
      </div>
    </div>
  );
}
